import { Divide, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { dividirValor } from "../lib/divisao";
import { brl } from "../lib/formato";

const MIN_PARTES = 2;
const MAX_PARTES = 50;
const ATALHOS = [2, 3, 4, 5, 6, 8];

function Previa({ valor, partes }) {
  const valores = dividirValor(valor, partes);
  const primeira = valores[0];
  const ultima = valores[valores.length - 1];
  const exato = Math.abs(primeira - ultima) < 0.005;
  return (
    <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 text-center">
      <p className="text-4xl font-black text-primary tabular-nums">{brl(primeira)}</p>
      <p className="text-sm text-muted-foreground mt-1">para cada uma das {partes} partes</p>
      {!exato && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 tabular-nums">
          {partes - 1}× {brl(primeira)} + a última de {brl(ultima)} (sobra de centavos)
        </p>
      )}
    </div>
  );
}

export default function DividirDialog({ open, onOpenChange, valor, descricao, temDesconto, onConfirmar }) {
  // Texto livre no campo: travar no mínimo a cada tecla impedia digitar "12"
  const [texto, setTexto] = useState(String(MIN_PARTES));
  useEffect(() => { if (open) setTexto(String(MIN_PARTES)); }, [open]);

  const digitado = parseInt(texto, 10) || 0;
  const valido = digitado >= MIN_PARTES && digitado <= MAX_PARTES;
  const partes = valido ? digitado : MIN_PARTES;
  const ajustar = (n) => setTexto(String(Math.max(MIN_PARTES, Math.min(MAX_PARTES, n))));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Divide className="w-5 h-5 text-amber-500" /> Dividir em quantas partes?</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {descricao}: <span className="font-bold text-foreground tabular-nums">{brl(valor)}</span>
            {temDesconto && <span className="block text-xs text-green-600 dark:text-green-400">Já com o desconto aplicado</span>}
          </div>
          <div className="flex items-center justify-center gap-3">
            <Button type="button" variant="outline" size="icon" className="h-14 w-14" onClick={() => ajustar(partes - 1)} disabled={partes <= MIN_PARTES}>
              <Minus className="w-5 h-5" />
            </Button>
            <input
              type="text"
              inputMode="numeric"
              value={texto}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setTexto(e.target.value.replace(/\D/g, "").slice(0, 2))}
              onBlur={() => { if (!valido) ajustar(digitado); }}
              className="w-24 h-14 text-center text-3xl font-black bg-muted rounded-lg outline-none focus:ring-2 focus:ring-primary tabular-nums"
              aria-label="Quantidade de partes"
            />
            <Button type="button" variant="outline" size="icon" className="h-14 w-14" onClick={() => ajustar(partes + 1)} disabled={partes >= MAX_PARTES}>
              <Plus className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-1.5">
            {ATALHOS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => ajustar(n)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${valido && partes === n ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              >
                {n}×
              </button>
            ))}
          </div>
          {valido
            ? <Previa valor={valor} partes={partes} />
            : <p className="text-sm text-center text-amber-600 dark:text-amber-400">Informe de {MIN_PARTES} a {MAX_PARTES} partes.</p>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="button" className="flex-[1.5] h-11 font-bold" disabled={!valido} onClick={() => onConfirmar(partes)}>
              Dividir em {partes}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
